import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Card } from "@/components";
import apiClient, { AgentDiagnosticsResponse } from "@/lib/apiClient";

const statTile = (label: string, value: string | number) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p className="mt-1 text-lg font-semibold text-slate-900">{value}</p>
  </div>
);

const latestRunsTable = (diagnostics?: AgentDiagnosticsResponse) => {
  const rows = diagnostics?.global.runs.latest ?? [];
  if (rows.length === 0) return <p className="text-sm text-slate-500">No FLAME runs yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Run</th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">State</th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Reason</th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Updated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => (
            <tr key={row.run_id}>
              <td className="px-3 py-2 font-mono text-xs text-slate-700">{row.run_id}</td>
              <td className="px-3 py-2">{row.state}</td>
              <td className="px-3 py-2">{row.trigger_reason || "-"}</td>
              <td className="px-3 py-2">{row.updated_at || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const memoryList = (title: string, memories: AgentDiagnosticsResponse["global"]["memories"]["latest_reflections"]) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
    <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
    {memories.length === 0 ? (
      <p className="mt-2 text-sm text-slate-500">No memories available.</p>
    ) : (
      <div className="mt-3 space-y-3">
        {memories.map((memory) => (
          <div key={memory.memory_id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
              <span className="font-mono text-slate-700">{memory.memory_id}</span>
              <span>{memory.created_at || "unknown time"}</span>
              <span>confidence {memory.confidence.toFixed(2)}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              {(memory.tags || []).map((tag) => (
                <span key={`${memory.memory_id}-${tag}`} className="rounded bg-slate-200 px-2 py-0.5 text-slate-700">
                  {tag}
                </span>
              ))}
              {(memory.derived_from || []).map((session) => (
                <span key={`${memory.memory_id}-${session}`} className="rounded bg-slate-200 px-2 py-0.5 font-mono text-slate-700">
                  {session}
                </span>
              ))}
            </div>
            <pre className="mt-3 whitespace-pre-wrap break-words rounded bg-white p-2 font-sans text-sm text-slate-800">
              {memory.content || "(empty memory content)"}
            </pre>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const DevPage: React.FC = () => {
  const [selectedAgent, setSelectedAgent] = useState<string>("");
  const [sessionId, setSessionId] = useState<string>("");

  const agentsQuery = useQuery({
    queryKey: ["dev-agents"],
    queryFn: apiClient.getDevAgents,
  });

  const diagnosticsQuery = useQuery({
    queryKey: ["dev-diagnostics", selectedAgent, sessionId],
    queryFn: () => apiClient.getDevAgentDiagnostics(selectedAgent, sessionId || undefined, 20),
    enabled: Boolean(selectedAgent),
  });

  const logsQuery = useQuery({
    queryKey: ["dev-logs"],
    queryFn: () => apiClient.getDevLogs({ log_type: "all" }, 25, 0),
  });

  const diagnostics = diagnosticsQuery.data;
  const agentList = useMemo(() => agentsQuery.data?.agents || [], [agentsQuery.data]);

  return (
    <div className="space-y-6">
      <Card
        title="Developer Diagnostics"
        subtitle="Technical-only observability for LangChain runtime"
        className="border-slate-200"
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Agent</label>
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm"
            >
              <option value="">Select an agent</option>
              {agentList.map((agent) => (
                <option key={agent} value={agent}>
                  {agent}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Session ID (optional)</label>
            <input
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm"
              placeholder="ses_..."
            />
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            <p>Diagnostics refresh when you change agent or session.</p>
            <p className="mt-1">Errors are strict and come directly from runtime availability checks.</p>
          </div>
        </div>

        {diagnosticsQuery.isError ? (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            Failed to load diagnostics.{" "}
            {String(
              (
                diagnosticsQuery.error as {
                  response?: { data?: { detail?: string } };
                }
              )?.response?.data?.detail || ""
            )}
          </div>
        ) : null}

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-5">
          {statTile("FLAME Triggered", diagnostics?.global.flame.is_triggered ? "Yes" : "No")}
          {statTile("Runs", diagnostics?.global.runs.total ?? 0)}
          {statTile("Failed Runs", diagnostics?.global.runs.failed_count ?? 0)}
          {statTile("Reflection Memories", diagnostics?.global.memories.reflection_count ?? 0)}
          {statTile("Temp Memories", diagnostics?.global.memories.temp_count ?? 0)}
        </div>

        {diagnostics?.session ? (
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            {statTile("Session Pending Pool", diagnostics.session.pool_pending_count)}
            {statTile("Session Reflections", diagnostics.session.reflection_memory_count)}
            {statTile("Session Temp Memories", diagnostics.session.temp_memory_count)}
          </div>
        ) : null}

        <div className="mt-5">{latestRunsTable(diagnostics)}</div>

        <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-2">
          {memoryList("Latest Reflection Memories", diagnostics?.global.memories.latest_reflections || [])}
          {memoryList("Latest Temp Memories", diagnostics?.global.memories.latest_temp || [])}
        </div>
      </Card>

      <Card title="Runtime Logs" subtitle="Recent agent run events" className="border-slate-200">
        {logsQuery.isError ? (
          <p className="text-sm text-red-700">Failed to load logs.</p>
        ) : logsQuery.isLoading ? (
          <p className="text-sm text-slate-500">Loading logs...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Agent</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Session</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(logsQuery.data?.logs || []).map((row) => (
                  <tr key={`${row.type}-${row.id}`}>
                    <td className="px-3 py-2">{row.type}</td>
                    <td className="px-3 py-2">{row.agent_name || "-"}</td>
                    <td className="px-3 py-2">{row.status || row.input_status || "-"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.session_id || "-"}</td>
                    <td className="px-3 py-2">{row.created_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
